import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import Alert from 'react-bootstrap/Alert';
import { useHistory } from 'react-router-dom';

import VoiceSearchForm from 'components/LM/Voice/Search/VoiceSearchForm';
import VoiceProfileBadge from 'components/LM/Voice/VoiceProfileBadge';
import AdvancedTable from 'components/LM/common/AdvancedTable';
import { ToastService } from "@launchmetrics/lm-ui";

import K2Api from 'lib/ajax/K2Api';

import Breadcrumb from 'components/LM/common/Breadcrumb';
import './voiceSearch.scss';

export const TABLE_COLUMNS = [
	{
		name: "name",
		alias: "Voice"
	},
	{
		name: "type",
		alias: "Type"
	},
	{
		name: "country",
		alias: "Country"
	},
	{
		name: "socialmedia",
		alias: "Social"
	},
	{
		name: "online",
		alias: "Online"
	},
	{
		name: "print",
		alias: "Print"
	},
	{
		name: "url",
		alias: "Urls"
	}
];

const SIMILAR_COLUMNS = {
	name: "similarVoices",
	alias: "Similar Voices",
};


const SUPPORTED_PROFILES = new Set(['instagram', 'youtube', 'tiktok', 'twitter', 'facebook', 'weibo', 'douyin', 'xiaohongshu', 'bilibili', 'wechat_public']);

function VoiceSearch(props) {
	const { searchParams } = props;
	const [searchInputForm, setSearchInputForm] = useState()
	const history = useHistory()

	const [loading, setLoading] = useState(false);
	const [result, setResult] = useState();
	const [similarVoices, setSimilarVoices] = useState({});
	const [voicesCrawling, setVoicesCrawling] = useState({});
	const [error, setError] = useState();
	const [mergeItems, setMergeItems] = useState([]);

	useEffect(() => {
		// avoid twice calls
		if (!searchParams.term || searchParams.term === undefined || searchInputForm) {
			return;
		};
		const search = async () => {
			setLoading(true)
			setResult(null)
			try {
				const response = await doSearch(searchParams, { page: 1, pageSize: 20 });
				setResult(response);
			} catch (error) {
				setError(extractSearchErrorMessage(error));
			} finally {
				setLoading(false);
			}
		};
		search();

	}, [searchParams, searchInputForm]);

	useEffect(() => {
		if (!result?.voices) return;

		const fetchSocialCrawling = async () => {
			const k2Api = new K2Api();

			const updates = {};
			await Promise.all(result.voices.map(async voice => {
				try {
					const res = await k2Api.get(`/voices/${voice.id}/crawling`);
					let crawlDatas = res.data;
					Object.values(crawlDatas).forEach(value => {
						if (value && value.length > 0) {
							value.forEach(item => {
								if (!item?.enabled) {
									updates[`${voice.id}_${item.id}`] = false;
								}
							});
						}
					})
				} catch (e) {
					ToastService.show({
						id: "fetch-voice-crawling-failed",
						title: "Failed to fetch voice crawling",
						type: "error",
					});
				}
			}));

			setVoicesCrawling(prev => ({ ...prev, ...updates }));
		};

		fetchSocialCrawling();
	}, [result]);

	useEffect(() => {
		if (!result?.voices) return;

		const fetchSimilarVoices = async () => {
			const k2Api = new K2Api();

			const updates = {};
			await Promise.all(result.voices.map(async voice => {
				try {
					const res = await k2Api.get(`/voices/${voice.id}/merge/suggestions`);
					updates[voice.id] = res.data;
				} catch (e) {
					ToastService.show({
						id: "fetch-voice-suggestions-failed",
						title: "Failed to fetch voice suggestions",
						type: "error",
					});
				}
			}));

			setSimilarVoices(prev => ({ ...prev, ...updates }));
		};

		fetchSimilarVoices();
	}, [result]);


	const onFormChange = (inputFormValue, selectedValues) => {
		setSearchInputForm(inputFormValue);

		const params = new URLSearchParams({
			term: inputFormValue,
			type: selectedValues.type.join(','),
			channel: selectedValues.channel.join(','),
			country: selectedValues.country.join(','),
		});
		history.replace({ search: params.toString() });
	};

	const handleChecked = (checkedItems) => {
		setMergeItems(checkedItems);
	};

	const onMergeClick = (mergeItems) => {
		history.push({
			pathname: "/voices/merge",
			search: `?ids=${mergeItems.map(id => id).join(",")}`
		});
	};

	const isProcessable = mergeItems.length > 1 && mergeItems.length <= 5;

	return (
		<>
			<div id="voice_search">
				<Button className="float-right ml-2" href={`/voices/new`}>New Voice</Button>
				<Button variant="warning" className="float-right" disabled={!isProcessable} onClick={() => onMergeClick(mergeItems)}>Merge Voices</Button>
				<Breadcrumb items={['Voices', 'Search']} />
				{mergeItems.length > 5 ? <div> <Alert variant="danger">You can only merge up to 5 voices at a time</Alert> </div> : null}
				<AdvancedTable
						columns={[...TABLE_COLUMNS, SIMILAR_COLUMNS]}
						onSearch={async params => {
							try {
								return await doSearch(searchParams, params);
							} catch (err) {
								setResult(null);
								throw new Error(extractSearchErrorMessage(err));
							}
						}}
						onResults={response => {
							setResult(response);
							return processResponse(response, similarVoices, voicesCrawling);
						}}
						data={processResponse(result, similarVoices, voicesCrawling)}
						initialError={error}
						selectable={true}
						onSelectRows={checkedItems => handleChecked(checkedItems)}
					>
						<VoiceSearchForm searchParams={searchParams} onChange={onFormChange} countryMenu={result?.facets.country} loading={loading} totalMatches={result?.total} />
					</AdvancedTable>
			</div>
		</>
	);
}

export function extractSearchErrorMessage(error) {
	const backendMessage = error?.response?.data?.message;
	const isInvalidSyntax =
		error?.response?.status === 400 &&
		typeof backendMessage === 'string' &&
		backendMessage.toLowerCase().includes('invalid query syntax');

	if (isInvalidSyntax) {
		return 'Invalid query syntax. Please modify your search term and try again. For URL searches, wrap the full URL in double quotes, e.g. "https://www.instagram.com/james_sleaford".';
	}
	if (typeof backendMessage === 'string' && backendMessage.trim().length > 0) {
		return backendMessage;
	}
	return 'Search request failed. Please retry in a few moments.';
}

async function doSearch(searchParams, { page, pageSize }) {
	const from = (page - 1) * pageSize;
	const size = pageSize;
	const request = { ...searchParams, from, size };

	const k2Api = new K2Api();
	return (await k2Api.get('/voices/search', { params: request })).data;
}

export function processResponse(response, similarVoices = {}, voicesCrawling = {}) {
	if (!response) return;
	const buildSocialmediaBadges = (voiceId, socialmedia) => {
		if (!socialmedia) return;

		const badges = [];
		SUPPORTED_PROFILES.forEach(type => {
			if (!socialmedia[type]) return;

			socialmedia[type].forEach(profile => {
				badges.push(
					<a key={`${type}_${profile.id}`} href={`/${type}/profile?id=${profile.id}`}>
						<VoiceProfileBadge
							key={`${type}_${profile.id}`}
							type={type}
							name={profile.name}
							isRestricted={profile.is_restricted}
							isActive={voicesCrawling.hasOwnProperty(`${voiceId}_${profile.id}`)
								? voicesCrawling[`${voiceId}_${profile.id}`]
								: true}
						/>
					</a>
				);
			});
		});
		return badges;
	}

	const buildOnlineBadges = (onlineMedias) => {
		if (!onlineMedias) return;
		return onlineMedias.map(media =>
			<VoiceProfileBadge
				key={`online_${media.id}`}
				type='online'
				name={media.name}
				isActive={media?.status?.value !== 'inactive'}
			/>);
	}

	const buildPrintBadges = (printMedias) => {
		if (!printMedias) return;
		return printMedias.map(media =>
			<VoiceProfileBadge
				key={`print_${media.id}`}
				type='print'
				name={media.name}
				isActive={media?.status?.value !== 'inactive'} />);
	}

	const buildUrlBadges = (urls) => {
		if (!urls || urls.length === 0) return;
		return urls.map(item =>
			<a key={`url_${item.id}`} href={item.url} target="_blank" rel="noopener noreferrer">
				<VoiceProfileBadge
					key={`url_${item.id}`}
					type="link"
					name={item.url} />
			</a>);
	}

	const rows = response.voices.map(voice => {
		return {
			id: voice.id,
			name: (
				<a
					href={`/voices/${voice.id}`}
					target="_blank"
					rel="noopener noreferrer"
				>
					{voice.name}
				</a>
			),
			type: voice.type,
			country: voice.country && voice.country.name ? voice.country.name : '',
			socialmedia: buildSocialmediaBadges(voice.id, voice.socialmedia),
			online: buildOnlineBadges(voice.medias.online),
			print: buildPrintBadges(voice.medias.print),
			url: buildUrlBadges(voice.urls),
			similarVoices: similarVoices.hasOwnProperty(voice.id)
				? similarVoices[voice.id].length.toString()
				: 'Loading...'
		}
	});

	return { total: response.total, rows };
}

VoiceSearch.propTypes = {
	term: PropTypes.string
}

export default VoiceSearch;
